import { useState, useEffect, useRef } from 'react'
import { useGeneratePresignedGetUrlMutation } from 'lib/apollo/hooks'

const extractS3KeyFromUrl = (url: string): string | null => {
  if (!url) return null

  try {
    // Skip processing if this is a data URL (base64 encoded image)
    if (url.startsWith('data:')) {
      return null
    }

    // Skip blob URLs (locally generated, e.g., from camera captures)
    if (url.startsWith('blob:')) {
      return null
    }

    // Handle full S3 URLs
    const fullUrlMatch = url.match(/amazonaws\.com\/(.+)/)
    if (fullUrlMatch && fullUrlMatch[1]) {
      return fullUrlMatch[1]
    }

    // Handle URLs that are just S3 keys without the domain
    // Common prefixes for S3 keys in this application
    const commonPrefixes = [
      'tray_images/',
      'inventory_sheets/',
      'ifu_files/',
      'documents/',
      'uploads/',
    ]

    // Check if the URL starts with any of the known S3 key prefixes
    for (const prefix of commonPrefixes) {
      if (url.startsWith(prefix)) {
        return url
      }
    }

    // Additional check: if the URL contains a file extension and doesn't have http/https,
    // it's likely just an S3 key
    if (
      !url.startsWith('http') &&
      (url.includes('.jpg') ||
        url.includes('.png') ||
        url.includes('.pdf') ||
        url.includes('.jpeg') ||
        url.includes('.gif') ||
        url.includes('.xlsx') ||
        url.includes('.docx'))
    ) {
      return url
    }

    return null
  } catch (error) {
    console.error('Error extracting S3 key from URL:', error)
    return null
  }
}

interface UseS3ImageProps {
  url: string | undefined
  defaultImage?: string
}

const useDTMS3Image = ({ url, defaultImage = '' }: UseS3ImageProps) => {
  const [imageUrl, setImageUrl] = useState<string>(url || defaultImage)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const { generatePresignedGetUrl } = useGeneratePresignedGetUrlMutation()

  // Store the original URL to prevent infinite loops
  const originalUrlRef = useRef<string | undefined>(url)
  const processingUrlRef = useRef<boolean>(false)

  useEffect(() => {
    // Skip if we're already processing this URL
    if (processingUrlRef.current) {
      return
    }

    // Skip if we've already processed this URL and have a valid presigned URL
    if (
      url === originalUrlRef.current &&
      imageUrl &&
      imageUrl.includes('X-Amz-Signature=')
    ) {
      return
    }

    if (!url) {
      setImageUrl(defaultImage)
      originalUrlRef.current = url
      return
    }

    const getSignedUrl = async () => {
      setIsLoading(true)
      setError(null)
      processingUrlRef.current = true

      try {
        // Check if this is already a presigned URL
        if (url.includes('X-Amz-Signature=')) {
          setImageUrl(url)
          originalUrlRef.current = url
          setIsLoading(false)
          processingUrlRef.current = false
          return
        }

        // Skip processing for data URLs or blob URLs
        if (url.startsWith('data:') || url.startsWith('blob:')) {
          setImageUrl(url)
          originalUrlRef.current = url
          setIsLoading(false)
          processingUrlRef.current = false
          return
        }

        // Extract the S3 key from the URL
        const key = extractS3KeyFromUrl(url)

        if (!key) {
          // If we couldn't extract a key, just use the original URL
          setImageUrl(url)
          originalUrlRef.current = url
          setIsLoading(false)
          processingUrlRef.current = false
          return
        }

        // Get the presigned URL for the S3 key
        const presignedUrl = await generatePresignedGetUrl(key)

        if (presignedUrl) {
          setImageUrl(presignedUrl)
          originalUrlRef.current = url // Only update originalUrlRef after successful processing
        } else {
          // If we couldn't get a presigned URL, fallback to the original URL
          setImageUrl(url)
          originalUrlRef.current = url
        }
      } catch (error) {
        console.error('Error getting presigned URL:', error)
        setError('Failed to load image')
        setImageUrl(defaultImage)
        // Don't update originalUrlRef on error to allow retrying
      } finally {
        setIsLoading(false)
        processingUrlRef.current = false
      }
    }

    getSignedUrl()
  }, [url, defaultImage, generatePresignedGetUrl])

  return {
    imageUrl,
    isLoading,
    error,
  }
}

export default useDTMS3Image
